import React from "react";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import { CircularProgress } from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { useRecentEvaluationsModalViewModel } from "./useRecentEvaluationsModalViewModel";
import {
	ModalPaper,
	ModalTop,
	ModalTitleWrap,
	ModalIcon,
	ModalTitle,
	CloseButton,
	ModalDescription,
	ModalContent,
	PatientSearch,
	PatientList,
	SkeletonRow,
	PatientItem,
	PatientSummary,
	ListAvatar,
	ListPhoto,
	PatientInfo,
	ListName,
	ListMeta,
	SendReportButton,
	LoadMoreContainer,
	LoadMoreButton,
	NoResults,
} from "./styles";

import { ReactComponent as CloseIcon } from "../../../../assets/kinologyIcons/svg/close.svg";
import { ReactComponent as GrahDashIcon } from "../../../../assets/kinologyIcons/svg/bar_chart_dash.svg";
import { ReactComponent as SearchIcon } from "../../../../assets/kinologyIcons/svg/search.svg";
import { ReactComponent as FallbackIcon } from "../../../../assets/kinologyIcons/svg/patient.svg";

const useStyles = makeStyles(() =>
	createStyles({
		modal: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
	})
);

interface RecentEvaluationsModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const RecentEvaluationsModal: React.FC<RecentEvaluationsModalProps> = ({
	isOpen,
	onClose,
}) => {
	const classes = useStyles();
	const {
		searchQuery,
		isLoading,
		isLoadingMore,
		sendingReportPatientId,
		loadMoreCount,
		filteredPatients,
		hasExtraPatients,
		totalPatients,
		error,
		handleSearchChange,
		handleSendReport,
		handleLoadMore,
		retry,
	} = useRecentEvaluationsModalViewModel(isOpen, onClose);

	return (
		<Modal
			aria-labelledby="recent-modal-title"
			aria-describedby="recent-modal-description"
			className={classes.modal}
			open={isOpen}
			onClose={onClose}
			closeAfterTransition
			BackdropComponent={Backdrop}
			BackdropProps={{
				timeout: 300,
			}}
		>
			<Fade in={isOpen}>
				<ModalPaper>
					<ModalTop>
						<ModalTitleWrap>
							<ModalIcon>
								<GrahDashIcon />
							</ModalIcon>
							<ModalTitle id="recent-modal-title">
								Avaliados nos últimos 30 dias
							</ModalTitle>
						</ModalTitleWrap>
						<CloseButton type="button" onClick={onClose} aria-label="Fechar">
							<CloseIcon width={15} height={15} />
						</CloseButton>
					</ModalTop>

					<ModalDescription>
						<p id="recent-modal-description">
							Estes {totalPatients} pacientes fizeram sua avaliação nos últimos
							30 dias.
						</p>
					</ModalDescription>

					<ModalContent>
						<PatientSearch>
							<SearchIcon />
							<input
								type="text"
								placeholder="Buscar paciente..."
								value={searchQuery}
								onChange={(e) => handleSearchChange(e.target.value)}
							/>
						</PatientSearch>

						{error && filteredPatients.length === 0 ? (
							<NoResults>
								Não foi possível carregar os pacientes.{" "}
								<button type="button" onClick={retry}>
									Tentar novamente
								</button>
							</NoResults>
						) : isLoading && !isLoadingMore ? (
							<PatientList aria-label="Carregando pacientes">
								<SkeletonRow />
								<SkeletonRow />
								<SkeletonRow />
								<SkeletonRow />
							</PatientList>
						) : filteredPatients.length > 0 ? (
							<PatientList>
								{filteredPatients.map((p) => (
									<PatientItem key={p.id}>
										<PatientSummary>
											<ListAvatar hasPhoto={!!p.photoUrl}>
												{p.photoUrl ? (
													<ListPhoto src={p.photoUrl} alt={p.name} />
												) : (
													<FallbackIcon
														width="20px"
														height="20px"
														fill="#ffffff"
													/>
												)}
											</ListAvatar>
											<PatientInfo>
												<ListName>{p.name}</ListName>
												<ListMeta>
													{p.protocolType}:{" "}
													<span
														style={{ fontWeight: "bold", color: "#111827" }}
													>
														{p.protocol}
													</span>
												</ListMeta>
											</PatientInfo>
											<SendReportButton
												type="button"
												disabled={!!sendingReportPatientId}
												onClick={() => handleSendReport(p.id)}
											>
												{sendingReportPatientId === p.id ? (
													<CircularProgress
														size={14}
														style={{ color: "#00a377" }}
													/>
												) : (
													"Enviar relatório"
												)}
											</SendReportButton>
										</PatientSummary>
									</PatientItem>
								))}
							</PatientList>
						) : (
							<NoResults>Nenhum paciente encontrado.</NoResults>
						)}

						{hasExtraPatients && (
							<LoadMoreButton
								type="button"
								onClick={!isLoadingMore ? handleLoadMore : undefined}
								disabled={isLoadingMore}
							>
								{isLoadingMore
									? "Carregando..."
									: `Carregar mais ${loadMoreCount} pacientes`}
							</LoadMoreButton>
						)}
					</ModalContent>
				</ModalPaper>
			</Fade>
		</Modal>
	);
};

export default RecentEvaluationsModal;
